<template>
  <GamePane product="cockfight" :wide="wide">
    <template #title>
      <div class="cockfight-pane__identity">
        <strong>{{ arena.code }}</strong>
        <span v-if="arenaName" class="cockfight-pane__name">· {{ arenaName }}</span>
      </div>
    </template>

    <template #video>
      <CockfightVideo :arena-id="arena.id" />
    </template>

    <!--
      The design splits what BetPanel bundles: fight status + jackpot sit in the
      info band, the arena rail in the selector band below it.
    -->
    <template #info>
      <GameStatus
        class="cockfight-pane__status"
        :active="active"
        :show-arenas="false"
        @jackpotCollectPopup="$emit('jackpot')"
        @jackpotPhysicalPopup="$emit('jackpot-physical')"
      />
    </template>

    <!--
      Single view: the roads move to the left column, under the video, which is
      where the v1 desktop cockfight page has always put them.
    -->
    <template v-if="wide" #aside>
      <Charts v-if="chartFights && chartFights.length > 0" fluid class="cockfight-pane__roads" />
    </template>

    <!--
      Split view: the rail is its own band under the video. Single view: it sits
      in the right column directly under the bet zone, as the design shows, so
      it is handed to BetPanel's slot instead.
    -->
    <template v-if="!wide" #selector>
      <ArenaChips :items="arenaItems" @select="$emit('select-arena', $event)" />
    </template>

    <BetPanel
      class="cockfight-pane__betting"
      :class="{ 'cockfight-pane__betting--wide': wide }"
      :active="active"
      :show-status="false"
    >
      <template v-if="wide" #under-zone>
        <ArenaChips
          class="cockfight-pane__rail"
          :items="arenaItems"
          @select="$emit('select-arena', $event)"
        />
      </template>
    </BetPanel>
    <Charts v-if="!wide && chartFights && chartFights.length > 0" fluid class="cockfight-pane__roads" />
  </GamePane>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { acquireCockfightPaneStore, cockfightPaneNamespace, releaseCockfightPaneStore } from '@/store/cockfight-pane';
import { FightStatus } from '@/utils/index.model';
import GamePane from '@/components/desktop/game-pane.vue';
import CockfightVideo from '@/components/desktop/cockfight-video.vue';
import BetPanel from '@/components/bet-panel';
import GameStatus from '@/components/bet-panel/game-status.vue';
import ArenaChips from '@/components/desktop/arena-chips.vue';
import Charts from '@/components/charts/index.vue';

// Mirrors cock-fight.vue: the road is refetched this long after a result, once
// the backend has recorded it.
const CHART_REFRESH_DELAY = 15000;

/*
 * A desktop pane showing a cockfight arena.
 *
 * BetPanel, GameStatus, Charts and the players are the mobile components,
 * bound by name to `fight/*`, `bets/*` and `general/selectedArena`. So that two
 * panes can show two arenas, this pane replaces `$store` for its whole subtree
 * with a scoped view (see `store/cockfight-pane.ts`) in which those names
 * resolve to this pane's own copies. Everything else -- balance, config,
 * jackpot -- is still the shared root store.
 *
 * The desktop client renders this pane directly instead of going through
 * cock-fight.vue, so the watchers below are that page's orchestration (load the
 * live fight, the road, my stakes, the stream) scoped to the pane's arena.
 */
export default {
  name: 'CockfightPane',
  components: {
    GamePane,
    CockfightVideo,
    BetPanel,
    GameStatus,
    ArenaChips,
    Charts,
  },
  props: {
    paneId: {
      type: String,
      required: true,
    },
    arenaId: {
      type: String,
      default: '',
    },
    // The combined cockfight + Tài Xỉu rail, prepared by the desktop client.
    arenaItems: {
      type: Array,
      default: () => [],
    },
    // Single view: the pane fills the row in two columns.
    wide: {
      type: Boolean,
      default: false,
    },
    // Mirrors cock-fight.vue: highlights the jackpot control when a ticket is uncollected.
    active: {
      type: Boolean,
      default: false,
    },
  },
  /*
   * Runs after Vuex's own beforeCreate has set `$store` to the root, and before
   * any child is created -- children inherit `$store` from their parent, so
   * the whole subtree picks up the scoped view. Props are not initialised yet,
   * hence `propsData`.
   */
  beforeCreate() {
    const { paneId, arenaId } = this.$options.propsData;
    this.$rootStore = this.$store;
    this.$store = acquireCockfightPaneStore(this.$rootStore, paneId, arenaId || '');
  },
  computed: {
    ...mapGetters({
      arena: 'general/selectedArena',
      config: 'general/config',
    }),
    ...mapGetters('fight', ['fight', 'chartFights']),
    arenaName() {
      const { name } = this.arena;
      if (!name || typeof name === 'string') return name;
      return name[this.config.language] || name.default;
    },
  },
  watch: {
    arenaId: {
      immediate: true,
      handler(arenaId) {
        this.$rootStore.commit(`${cockfightPaneNamespace(this.paneId)}/SET_ARENA`, arenaId || '');
      },
    },
    'arena.id': {
      immediate: true,
      handler(arenaId, previousArenaId) {
        if (previousArenaId) this.stopArenaStreamPolling(previousArenaId);
        this.resetFight();
        this.resetFights();
        this.resetFightsPage();
        this.resetBetData();
        this.resetBetHistory();
        this.resetMyBets();
        this.clearChart();
        if (!arenaId) return;
        this.getCurrentFight({ arena_id: arenaId });
        this.getFightsForChart(this.arena.currentMatchDayId);
        this.getArenaStreamUrl(arenaId);
      },
    },
    // Mirrors cock-fight.vue: load this member's stakes for the live fight.
    'fight.id': {
      immediate: true,
      handler(fightId) {
        if (!fightId) return;
        this.getMyBets(fightId);
      },
    },
    // Mirrors cock-fight.vue: drop unsent chips once betting closes, and pull the
    // road again after a result.
    'fight.status'(status) {
      if (!status) {
        this.resetBetData();
        this.resetBetHistory();
      } else if (FightStatus[status] >= 3) {
        this.dropUnconfirmedBets();
      }
      if (FightStatus[status] >= 7 || status === 'processing') {
        clearTimeout(this.chartTimer);
        const matchDayId = this.fight.matchDay && this.fight.matchDay.id;
        this.chartTimer = setTimeout(() => this.getFightsForChart(matchDayId), CHART_REFRESH_DELAY);
      }
    },
    '$screen.visibility'(isVisible, wasVisible) {
      if (isVisible && wasVisible === false && this.arena.id) {
        this.getCurrentFight({ arena_id: this.arena.id });
      }
    },
  },
  beforeDestroy() {
    clearTimeout(this.chartTimer);
    if (this.arena.id) this.stopArenaStreamPolling(this.arena.id);
  },
  destroyed() {
    releaseCockfightPaneStore(this.$rootStore, this.paneId);
  },
  methods: {
    ...mapActions('general', ['getArenaStreamUrl', 'stopArenaStreamPolling']),
    ...mapActions('fight', [
      'getCurrentFight',
      'getFightsForChart',
      'resetFight',
      'resetFights',
      'resetFightsPage',
    ]),
    ...mapActions('bets', ['getMyBets', 'resetBetData', 'resetBetHistory', 'resetMyBets', 'dropUnconfirmedBets']),
    clearChart() {
      this.$store.commit('fight/INSERT_CHART_FIGHTS', []);
      this.$store.commit('fight/INSERT_CHART_FIGHTS_TOTAL', { bankerCount: 0, playerCount: 0, drawCount: 0 });
    },
  },
};
</script>

<style lang="scss" scoped>
/*
 * v2.1 prototype (CfBet / ChipRow / CfRoad). Split view: the bet zone runs the
 * pane's width (its artwork is a fixed-ratio SVG, so it is as tall as that
 * width makes it), the 48px chip row, then the roads in what is left. Single
 * view, right column: 200 down, the zone, 40, arena tabs 34, 24, chips 48; the
 * roads sit under the video.
 */
$chip-row-height: 48px;
$chip-row-wide-height: 64px; // single view, as the old UI

.cockfight-pane__identity {
  display: flex;
  align-items: baseline;
  gap: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 700;

  strong {
    text-transform: uppercase;
  }
}

.cockfight-pane__name {
  color: $ga-text-muted;
  font-weight: 400;
}

/*
 * The fight status and jackpot pill, as the pane's 34px info band under the
 * video (game-pane.vue): status left at 14/800, the gold JACKPOT pill right.
 * Doubled to outrank the panel's own rules.
 */
.cockfight-pane__status.game-status-panel {
  width: 100%;
  height: 34px;
  min-height: 34px;
  max-height: 34px;
  min-width: 0;
  padding: 0 8px 0 14px;
  margin-bottom: 0;
  border-radius: 0 0 10px 10px;
  background-image: $ga-info-bar-bg;
  overflow: hidden;
}

.cockfight-pane__status.game-status-panel ::v-deep {
  .status-text {
    font-size: 13px;
    font-weight: 700;
  }

  .jackpot-btn-wrapper .jackpot-physical-btn {
    height: 24px;
  }

  .jackpot-btn-wrapper .jackpot-btn {
    height: 24px;
    margin-right: 0;
    background-image: $ga-grad-gold;
    box-shadow: $ga-shadow-gold;

    img {
      width: 18px;
      height: 18px;

      &.active {
        width: 22px;
        height: 22px;
      }
    }

    .text {
      color: $ga-text-on-gold;
      font-size: 11.5px;
      letter-spacing: 0.06em;
    }
  }
}

.cockfight-pane__betting {
  // Never scaled: desktop-client.vue pins the panel's ratio to 1, so its box is
  // what is drawn and the column below it lines up.
  flex: 0 0 auto;
}

/*
 * The panel stacks zone, (single view: tabs) and chip row with its own 10px
 * margins and the -21px pull the 16:9 zone needs on v1; here the pane's 6px
 * band gap spaces them instead.
 */
.cockfight-pane__betting.cockfight-pane__betting ::v-deep {
  > div,
  > div:first-of-type {
    margin-top: 0;
  }

  > div + div {
    margin-top: 6px;
  }
}

.cockfight-pane__betting ::v-deep {

  .bet-zone-container {
    aspect-ratio: auto;
    height: auto;
    min-height: 0;
    min-width: 0;
    margin: 0;
  }

  /*
   * The chip row (v2.1 ChipRow): 48 tall, Undo a 32px button over an 8.5px
   * label, the 40px chips spread evenly, then an 84px BET.
   */
  .function-btns-container {
    height: $chip-row-height;
    min-height: $chip-row-height;
    min-width: 0;
    gap: 8px;
    justify-content: space-around;

    & > div {
      margin: 0;
    }

    .undo-wrapper {
      flex: 0 0 32px;
      width: 32px;
      gap: 2px;

      img.undo {
        width: 32px;
        height: 32px;
      }

      .undo-label {
        position: static;
        transform: none;
        color: #8fb3e0;
        font-size: 8.5px;
        font-weight: 600;
        letter-spacing: 0.04em;
        line-height: 1;
      }
    }

    .chip-wrapper {
      min-width: 40px;
      min-height: 40px;
      max-width: 40px;
      max-height: 40px;
      padding: 0;
    }

    .chip-value {
      font-size: 12px;
    }

    .chip-wrapper.active,
    .chip-wrapper:hover {
      border-color: transparent;
      box-shadow:
        0 0 0 2px $ga-pane-bg,
        0 0 0 4px #fff;
    }

    .custom-chip-field {
      flex: 0 0 40px;
      width: 40px;
      height: 40px;
    }

    .custom-chip-trigger {
      width: 40px;
      height: 40px;
    }

    .custom-chip.chip-wrapper {
      min-width: 40px;
      min-height: 40px;
      max-width: 40px;
      max-height: 40px;
    }

    /*
     * Bet: the Tài Xỉu pane's gold plate (bet-board-actions.vue), so the two
     * panes' rows match, at the prototype's 84x42.
     */
    .bet-btn-wrapper {
      flex: 0 0 84px;
      width: 84px;
      min-width: 84px;
      max-width: 84px;
      height: 42px;
      min-height: 42px;
      max-height: 42px;
      border-radius: 0;
      background: url('~@/assets/images/bet-btn-tx.webp') center/100% 100% no-repeat;
      animation: none;

      img {
        display: none;
      }

      .bet-btn-text,
      .bet-btn-text.en,
      .bet-btn-text.vn {
        font-size: 15px;
        font-weight: 800;
        line-height: 1;
        color: #59401a;
        text-shadow: none;
        text-transform: uppercase;
      }

      &.disabled {
        background-color: transparent;
      }
    }
  }
}

// Split view: the bet zone at v2's 0.9, so its odds and labels are not
// oversized. Single view keeps it at full column width.
.game-pane:not(.game-pane--wide) .cockfight-pane__betting ::v-deep .bet-zone-container {
  zoom: 0.9;
}

/*
 * Single view, as the old UI: the zone's bottom is level with the video's
 * (302 = 16:9 of the 536 column, game-pane.vue), the arena tabs share the row with the
 * video's info bar (34), and the chip row comes 8 under them, level with the
 * roads. A grid, so the zone sits on that line whatever its own height.
 */
.cockfight-pane__betting--wide.cockfight-pane__betting--wide {
  display: grid;
  // One full-width column: the panel's own flex `justify-content` would
  // otherwise shrink the track to its content.
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: 302px 34px auto;
  row-gap: 0;
  align-content: start;
  justify-content: stretch;
  justify-items: stretch;
}

.cockfight-pane__betting--wide.cockfight-pane__betting--wide ::v-deep {
  > div + div {
    margin-top: 0;
  }

  // Ends 16 above the video's bottom, as the Tài Xỉu board, so the draw
  // circle clears the arena tabs.
  .bet-zone-container {
    width: 100%;
    align-self: end;
    margin-bottom: 16px;
  }

  /*
   * The old UI's larger chip row, as the Tài Xỉu pane's single view: 54px
   * chips, a 36px Undo and a 100x46 BET in a 64px row.
   */
  .function-btns-container {
    margin-top: 8px;
    height: $chip-row-wide-height;
    min-height: $chip-row-wide-height;
    gap: 10px;

    .undo-wrapper {
      flex: 0 0 40px;
      width: 40px;

      img.undo {
        width: 36px;
        height: 36px;
      }

      .undo-label {
        font-size: 10px;
      }
    }

    .chip-wrapper,
    .custom-chip.chip-wrapper {
      min-width: 54px;
      min-height: 54px;
      max-width: 54px;
      max-height: 54px;
    }

    .chip-value {
      font-size: 15px;
    }

    .custom-chip-field {
      flex: 0 0 54px;
      width: 54px;
      height: 54px;
    }

    .custom-chip-trigger {
      width: 54px;
      height: 54px;
    }

    .bet-btn-wrapper {
      flex: 0 0 100px;
      width: 100px;
      min-width: 100px;
      max-width: 100px;
      height: 46px;
      min-height: 46px;
      max-height: 46px;

      .bet-btn-text,
      .bet-btn-text.en,
      .bet-btn-text.vn {
        font-size: 17px;
      }
    }
  }
}

.cockfight-pane__betting--wide.cockfight-pane__betting--wide .cockfight-pane__rail.cockfight-pane__rail {
  margin: 0;
  align-self: center;
}

.cockfight-pane__betting .cockfight-pane__rail.cockfight-pane__rail {
  width: 100%;
  min-width: 0;
  height: 34px;
  min-height: 34px;
  box-sizing: border-box;
  margin: 40px 0 24px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

/*
 * The roads (CfRoad): dark cards. The charts are canvases drawn at fixed px, so
 * they are sized with a zoom; whatever they do not fill stays empty rather
 * than scrolling.
 */
/*
 * Split view: the roads are ~256 tall unzoomed (counts row, big road and its
 * minis beside the 228 bead) in the ~160 the chip row leaves -- 0.62.
 */
.cockfight-pane__roads {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
  zoom: 0.62;
  overflow: hidden;
}

// Single view: the roads fill the left column under the video, at 0.75 now the
// column is half the pane (it was 0.9 at 675 wide).
.game-pane--wide .cockfight-pane__roads {
  flex: 1 1 auto;
  min-height: 0;
  zoom: 0.75;
}

.cockfight-pane__roads ::v-deep {
  .symbals-wrapper {
    height: 22px;
    margin-bottom: 6px;
    font-weight: 700;
  }

  .road-chart,
  .small-charts > div {
    border: 1px solid $ga-road-border;
    border-radius: 6px;
    background-color: $ga-road-bg;
    overflow: hidden;
  }

  .right-charts {
    margin-right: 6px;
  }

  .small-charts {
    margin-top: 6px;

    & > div {
      margin-right: 6px;
    }
  }
}
</style>
